import React, { useState, useMemo } from 'react';
import { Card, Table, Tag, Button, Space, Typography, Input, Select, Modal, Form, DatePicker, InputNumber, Spin, Row, Col, Descriptions, Divider, Tabs, Collapse } from 'antd';
import { SearchOutlined, EyeOutlined, CalendarOutlined, FileTextOutlined, EditOutlined, CheckCircleOutlined, CloseCircleOutlined, HeartOutlined, TeamOutlined, DownloadOutlined, UserAddOutlined, ProfileOutlined, FilterOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import { companyProfileQueryKeys } from '../../../lib/queryClient';
import TabListLoading from '../../../components/Common/TabListLoading';
import { apiService } from '../../../services/api';
import { JobApplicationResponse, CandidateFilterRequest } from '../../../types';
import { ApiDefinition } from '../../../config';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { Panel } = Collapse;

interface Interview {
  id: number;
  jobApplicationId: number;
  applicantName?: string;
  jobTitle?: string;
  interviewDate: string;
  interviewType: string;
  status: string;
  notes?: string;
  interviewerName?: string;
  location?: string;
  interviewFeedback?: string;
  rating?: number;
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
}

const cleanFilters = (f: CandidateFilterRequest, searchTerm?: string, status?: string): CandidateFilterRequest | undefined => {
  const cleaned: Partial<CandidateFilterRequest> = {};
  Object.keys(f).forEach(key => {
    const value = f[key as keyof CandidateFilterRequest];
    if (value !== undefined && value !== null && value !== '') (cleaned as any)[key] = value;
  });
  if (searchTerm) (cleaned as any).searchTerm = searchTerm;
  if (status) (cleaned as any).status = status;
  return Object.keys(cleaned).length > 0 ? (cleaned as CandidateFilterRequest) : undefined;
};

const applicationStatusOptions = [
  { value: 'Pending', label: 'Beklemede', color: 'orange' },
  { value: 'Reviewed', label: 'İncelendi', color: 'blue' },
  { value: 'Accepted', label: 'Kabul Edildi', color: 'green' },
  { value: 'Rejected', label: 'Reddedildi', color: 'red' },
];

const ApplicationsTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedApplication, setSelectedApplication] = useState<JobApplicationResponse | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [interviewModalVisible, setInterviewModalVisible] = useState(false);
  const [interviewForm] = Form.useForm();
  const [editingInterview, setEditingInterview] = useState<Interview | null>(null);
  const [candidateDetailModalVisible, setCandidateDetailModalVisible] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [candidateDetailLoading, setCandidateDetailLoading] = useState(false);
  const [advancedFiltersVisible, setAdvancedFiltersVisible] = useState(false);
  const [filters, setFilters] = useState<CandidateFilterRequest>({});
  const [interviewPage, setInterviewPage] = useState(1);
  const interviewPageSize = 20;

  const applicationsFilter = useMemo(() => cleanFilters(filters, searchText, statusFilter), [filters, searchText, statusFilter]);

  const { data: applications = [], isLoading: loading, refetch: refetchApplications } = useQuery({
    queryKey: companyProfileQueryKeys.applications(applicationsFilter ?? {}),
    queryFn: async () => {
      const r = await apiService.getCompanyJobApplications(applicationsFilter);
      if (!r.success) throw new Error(r.message);
      return r.data ?? [];
    },
    staleTime: 1000 * 60 * 2,
  });

  const { data: interviewsResult, isLoading: interviewsLoading } = useQuery({
    queryKey: [companyProfileQueryKeys.interviews, interviewPage],
    queryFn: async () => {
      const r = await apiService.getCompanyInterviews(interviewPage, interviewPageSize);
      if (!r.success) throw new Error(r.message);
      return r.data;
    },
    staleTime: 1000 * 60 * 2,
  });
  const interviews = interviewsResult?.data ?? [];
  const interviewTotal = interviewsResult?.totalCount ?? 0;

  const { data: favorites = [] } = useQuery({
    queryKey: companyProfileQueryKeys.favorites({}),
    queryFn: async () => {
      const r = await apiService.getCandidateFavorites();
      if (!r.success) throw new Error(r.message);
      return r.data ?? [];
    },
    staleTime: 1000 * 60 * 2,
  });

  const { data: pool = [] } = useQuery({
    queryKey: companyProfileQueryKeys.pool({}),
    queryFn: async () => {
      const r = await apiService.getCandidatePool();
      if (!r.success) throw new Error(r.message);
      return r.data ?? [];
    },
    staleTime: 1000 * 60 * 2,
  });

  const { data: mentors = [] } = useQuery({
    queryKey: companyProfileQueryKeys.mentors,
    queryFn: async () => {
      const r = await apiService.searchMentors({ page: 1, pageSize: 100 });
      if (!r.success) throw new Error(r.message);
      return r.data?.data ?? [];
    },
    staleTime: 1000 * 60 * 10,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ applicationId, status }: { applicationId: number; status: string }) =>
      apiService.updateJobApplicationStatus(applicationId, status),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Başvuru durumu güncellendi');
        queryClient.invalidateQueries({ queryKey: ['company-panel', 'applications'] });
      } else toast.error(response.message || 'Durum güncellenirken hata oluştu');
    },
    onError: () => toast.error('Durum güncellenirken hata oluştu'),
  });

  const createInterviewMutation = useMutation({
    mutationFn: (data: any) => apiService.createInterview(data),
    onSuccess: (response) => {
      if (response.success) {
        setInterviewModalVisible(false);
        queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.interviews });
        toast.success('Mülakat planlandı');
      } else toast.error(response.message || 'Mülakat planlanırken hata oluştu');
    },
    onError: () => toast.error('Mülakat kaydedilirken hata oluştu'),
  });

  const updateInterviewMutation = useMutation({
    mutationFn: ({ id, values }: { id: number; values: any }) => apiService.updateInterview(id, values),
    onSuccess: (response) => {
      if (response.success) {
        setInterviewModalVisible(false);
        queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.interviews });
        toast.success('Mülakat güncellendi');
      } else toast.error(response.message || 'Mülakat güncellenirken hata oluştu');
    },
    onError: () => toast.error('Mülakat kaydedilirken hata oluştu'),
  });

  const addToFavoritesMutation = useMutation({
    mutationFn: (data: { menteeId: number | null | undefined; mentorId: number | null | undefined; notes: string }) =>
      apiService.addToFavorites({ ...data, menteeId: data.menteeId ?? null, mentorId: data.mentorId ?? null }),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Aday favorilere eklendi');
        queryClient.invalidateQueries({ queryKey: ['company-panel', 'favorites'] });
      } else toast.error(response.message || 'Favorilere eklenirken hata oluştu');
    },
    onError: () => toast.error('Favorilere eklenirken hata oluştu'),
  });

  const addToPoolMutation = useMutation({
    mutationFn: (data: { menteeId: number | null | undefined; mentorId: number | null | undefined; notes: string; status: string }) =>
      apiService.addToPool({ ...data, menteeId: data.menteeId ?? null, mentorId: data.mentorId ?? null }),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Aday havuza eklendi');
        queryClient.invalidateQueries({ queryKey: ['company-panel', 'pool'] });
      } else toast.error(response.message || 'Havuza eklenirken hata oluştu');
    },
    onError: () => toast.error('Havuza eklenirken hata oluştu'),
  });

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'orange';
      case 'reviewed':
        return 'blue';
      case 'accepted':
        return 'green';
      case 'rejected':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'Beklemede';
      case 'reviewed':
        return 'İncelendi';
      case 'accepted':
        return 'Kabul Edildi';
      case 'rejected':
        return 'Reddedildi';
      default:
        return status;
    }
  };

  const getInterviewStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'scheduled':
        return 'blue';
      case 'completed':
        return 'green';
      case 'cancelled':
        return 'red';
      case 'rescheduled':
        return 'orange';
      default:
        return 'default';
    }
  };

  const getInterviewStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'scheduled':
        return 'Planlandı';
      case 'completed':
        return 'Tamamlandı';
      case 'cancelled':
        return 'İptal Edildi';
      case 'rescheduled':
        return 'Yeniden Planlandı';
      default:
        return status;
    }
  };

  const handleViewDetails = (application: JobApplicationResponse) => {
    setSelectedApplication(application);
    setDetailModalVisible(true);
  };

  const handleScheduleInterview = (application: JobApplicationResponse) => {
    setSelectedApplication(application);
    setEditingInterview(null);
    interviewForm.resetFields();
    setInterviewModalVisible(true);
  };

  const handleEditInterview = (interview: Interview) => {
    setEditingInterview(interview);
    interviewForm.setFieldsValue({
      interviewDate: dayjs(interview.interviewDate),
      interviewType: interview.interviewType,
      status: interview.status,
      notes: interview.notes,
      interviewerName: interview.interviewerName,
      location: interview.location,
      interviewFeedback: interview.interviewFeedback,
      rating: interview.rating
    });
    setInterviewModalVisible(true);
  };

  const handleInterviewSubmit = async (values: any) => {
    try {
      if (editingInterview) {
        updateInterviewMutation.mutate({ id: editingInterview.id, values });
      } else if (selectedApplication) {
        createInterviewMutation.mutate({ jobApplicationId: selectedApplication.id, ...values });
      }
    } catch (error) {
      toast.error('Mülakat kaydedilirken hata oluştu');
    }
  };

  const handleUpdateApplicationStatus = (applicationId: number, status: string) => {
    updateStatusMutation.mutate({ applicationId, status });
  };

  const isInFavorites = (application: JobApplicationResponse) => {
    return favorites.some(fav => 
      (application.applicantType === 'Mentee' && fav.menteeId === application.menteeId) ||
      (application.applicantType === 'Mentor' && fav.mentorId === application.mentorId)
    );
  };

  const isInPool = (application: JobApplicationResponse) => {
    return pool.some(p => 
      (application.applicantType === 'Mentee' && p.menteeId === application.menteeId) ||
      (application.applicantType === 'Mentor' && p.mentorId === application.mentorId)
    );
  };

  const handleAddToFavorites = (application: JobApplicationResponse) => {
    addToFavoritesMutation.mutate({
      menteeId: application.applicantType === 'Mentee' ? application.menteeId : null,
      mentorId: application.applicantType === 'Mentor' ? application.mentorId : null,
      notes: `İş ilanı: ${application.jobTitle} - Başvuru tarihi: ${dayjs(application.appliedAt).format('DD.MM.YYYY')}`
    });
  };

  const handleAddToPool = (application: JobApplicationResponse) => {
    addToPoolMutation.mutate({
      menteeId: application.applicantType === 'Mentee' ? application.menteeId : null,
      mentorId: application.applicantType === 'Mentor' ? application.mentorId : null,
      notes: `İş ilanı: ${application.jobTitle} - Başvuru tarihi: ${dayjs(application.appliedAt).format('DD.MM.YYYY')}`,
      status: 'Available'
    });
  };

  const handleViewCandidateDetail = async (application: JobApplicationResponse) => {
    setSelectedCandidate(application);
    setCandidateDetailLoading(true);
    setCandidateDetailModalVisible(true);
    
    try {
      if (application.applicantType === 'Mentee' && application.menteeId) {
        // Temel bilgileri yükle
        const response = await apiService.getMenteeDetail(application.menteeId);
        if (response.success) {
          let detail: any = { ...response.data };
          
          // Backend field mapping - MenteeDetailResponse field'larını frontend formatına çevir
          detail.phoneNumber = response.data.phone;
          detail.city = (response.data as any).location || (response.data as any).city || null;
          detail.websiteUrl = response.data.website;
          detail.linkedInProfile = response.data.linkedIn;
          detail.githubProfile = response.data.github;
          detail.title = response.data.title || null;
          detail.hourlyRate = null; // Mentee'de yok
          
          // Detaylı bilgileri ayrı ayrı yükle
          try {
            const [skillsRes, educationsRes, workExpRes, projectsRes, certificatesRes] = await Promise.all([
              apiService.getMenteeSkillsById(application.menteeId),
              apiService.getMenteeEducationsById(application.menteeId),
              apiService.getMenteeWorkExperiencesById(application.menteeId),
              apiService.getMenteeProjectsById(application.menteeId),
              apiService.getMenteeCertificatesById(application.menteeId)
            ]);
            
            if (skillsRes.success && skillsRes.data) detail.skills = skillsRes.data;
            if (educationsRes.success && educationsRes.data) detail.educations = educationsRes.data;
            if (workExpRes.success && workExpRes.data) detail.workExperiences = workExpRes.data;
            if (projectsRes.success && projectsRes.data) detail.projects = projectsRes.data;
            if (certificatesRes.success && certificatesRes.data) detail.certificates = certificatesRes.data;
          } catch (err) {
            console.error('Error loading mentee detail sections:', err);
          }
          
          setSelectedCandidate({ ...application, detail });
        } else {
          toast.error('Mentee detayları yüklenemedi');
        }
      } else if (application.applicantType === 'Mentor' && application.mentorId) {
        // Temel bilgileri yükle
        const response = await apiService.getMentorDetail(application.mentorId);
        if (response.success) {
          let detail: any = { ...response.data };
          
          // Backend field mapping - MentorDetailResponse field'larını frontend formatına çevir
          detail.phoneNumber = response.data.phone;
          detail.city = (response.data as any).location || (response.data as any).city || null;
          detail.websiteUrl = response.data.website;
          detail.linkedInProfile = response.data.linkedIn;
          detail.githubProfile = response.data.github;
          detail.hourlyRate = response.data.hourlyRate;
          
          // Detaylı bilgileri ayrı ayrı yükle
          try {
            const [skillsRes, educationsRes, workExpRes, projectsRes, certificatesRes] = await Promise.all([
              apiService.getMentorSkills(application.mentorId),
              apiService.getMentorEducations(application.mentorId),
              apiService.getMentorWorkExperiences(application.mentorId),
              apiService.getMentorProjects(application.mentorId),
              apiService.getMentorCertificates(application.mentorId)
            ]);
            
            if (skillsRes.success && skillsRes.data) detail.skills = skillsRes.data;
            if (educationsRes.success && educationsRes.data) detail.educations = educationsRes.data;
            if (workExpRes.success && workExpRes.data) detail.workExperiences = workExpRes.data;
            if (projectsRes.success && projectsRes.data) detail.projects = projectsRes.data;
            if (certificatesRes.success && certificatesRes.data) detail.certificates = certificatesRes.data;
          } catch (err) {
            console.error('Error loading mentor detail sections:', err);
          }
          
          setSelectedCandidate({ ...application, detail });
        } else {
          toast.error('Mentor detayları yüklenemedi');
        }
      } else {
        toast.error('Aday ID\'si bulunamadı');
      }
    } catch (error) {
      console.error('Error loading candidate detail:', error);
      toast.error('Aday detayları yüklenirken hata oluştu');
    } finally {
      setCandidateDetailLoading(false);
    }
  };

  const handleDownloadResume = async (application: JobApplicationResponse) => {
    try {
      if (!application.resumeFileName) {
        toast.warning('CV bulunamadı');
        return;
      }

      const response = await fetch(`${ApiDefinition.apiUrl}/api/jobapplication/download-resume/${application.id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = application.resumeFileName || 'resume.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('CV indirildi');
      } else {
        toast.error('CV indirilemedi');
      }
    } catch (error) {
      console.error('Error downloading resume:', error);
      toast.error('CV indirilirken hata oluştu');
    }
  };

  const handleFilterChange = (key: keyof CandidateFilterRequest, value: any) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      if (value === undefined || value === null || value === '') {
        delete newFilters[key];
      } else {
        newFilters[key] = value;
      }
      return newFilters;
    });
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchText('');
    setStatusFilter('');
  };

  const applicationColumns = [
    {
      title: 'Aday',
      key: 'applicant',
      render: (record: JobApplicationResponse) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.applicantName || 'Bilinmiyor'}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>
            {record.applicantEmail}
          </div>
          <div style={{ color: '#666', fontSize: '12px' }}>
            {record.applicantType}
          </div>
        </div>
      ),
    },
    {
      title: 'İş İlanı',
      dataIndex: 'jobTitle',
      key: 'jobTitle',
      render: (text: string, record: JobApplicationResponse) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>
            {record.companyName}
          </div>
        </div>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Başvuru Tarihi',
      dataIndex: 'appliedAt',
      key: 'appliedAt',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: JobApplicationResponse) => (
        <Space wrap>
          <Button 
            type="link" 
            icon={<EyeOutlined />}
            onClick={() => handleViewCandidateDetail(record)}
          >
            Detay
          </Button>
          <Button 
            type="link" 
            icon={<ProfileOutlined />}
            onClick={() => {
              if (record.applicantType === 'Mentee' && record.menteeId) {
                window.open(`/mentee/${record.menteeId}`, '_blank');
              } else if (record.applicantType === 'Mentor' && record.mentorId) {
                window.open(`/mentor/${record.mentorId}`, '_blank');
              }
            }}
          >
            Profil Görüntüle
          </Button>
          <Button 
            type="link" 
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadResume(record)}
          >
            CV İndir
          </Button>
          <Button 
            type="link" 
            icon={<HeartOutlined />}
            onClick={() => handleAddToFavorites(record)}
            disabled={isInFavorites(record)}
            style={{ color: isInFavorites(record) ? 'var(--success)' : undefined }}
          >
            {isInFavorites(record) ? 'Favoride' : 'Favori'}
          </Button>
          <Button 
            type="link" 
            icon={<TeamOutlined />}
            onClick={() => handleAddToPool(record)}
            disabled={isInPool(record)}
            style={{ color: isInPool(record) ? 'var(--success)' : undefined }}
          >
            {isInPool(record) ? 'Havuzda' : 'Havuza Ekle'}
          </Button>
          <Button 
            type="link" 
            icon={<CalendarOutlined />}
            onClick={() => handleScheduleInterview(record)}
          >
            Mülakat Planla
          </Button>
          <Select
            value={record.status}
            onChange={(value) => handleUpdateApplicationStatus(record.id, value)}
            style={{ width: 140 }}
            size="small"
          >
            {applicationStatusOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Space>
      ),
    },
  ];

  const interviewColumns = [
    {
      title: 'Aday',
      key: 'applicant',
      render: (record: Interview) => {
        const application = applications.find(app => app.id === record.jobApplicationId);
        const applicantName = application?.applicantName || record.applicantName || 'Bilinmiyor';
        const jobTitle = application?.jobTitle || record.jobTitle || '-';
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{applicantName}</div>
            <div style={{ color: '#666', fontSize: '12px' }}>
              {jobTitle}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Mülakat Tarihi',
      dataIndex: 'interviewDate',
      key: 'interviewDate',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Tür',
      dataIndex: 'interviewType',
      key: 'interviewType',
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getInterviewStatusColor(status)}>
          {getInterviewStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Mülakatçı',
      dataIndex: 'interviewerName',
      key: 'interviewerName',
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: Interview) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => handleEditInterview(record)}
          >
            Düzenle
          </Button>
        </Space>
      ),
    },
  ];

  const listLoading = loading || interviewsLoading;

  return (
    <div className="panel-tab-content">
      <TabListLoading loading={listLoading}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Title level={4}>Başvuru Yönetimi</Title>
          <Text type="secondary">
            İş ilanlarınıza gelen başvuruları inceleyin, mülakat planlayın ve süreci yönetin.
          </Text>
        </div>

        <Tabs defaultActiveKey="applications">
          <TabPane tab="Başvurular" key="applications">
            {/* Temel Filtreler */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} md={8}>
                <Search
                  placeholder="Aday veya iş ilanı ara..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={() => refetchApplications()}
                  prefix={<SearchOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select
                  placeholder="Durum filtresi"
                  value={statusFilter}
                  onChange={(value) => {
                    setStatusFilter(value);
                    handleFilterChange('status', value);
                  }}
                  style={{ width: '100%' }}
                  allowClear
                >
                  {applicationStatusOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setAdvancedFiltersVisible(!advancedFiltersVisible)}
                  style={{ width: '100%' }}
                >
                  {advancedFiltersVisible ? 'Gelişmiş Filtreleri Gizle' : 'Gelişmiş Filtreler'}
                </Button>
              </Col>
            </Row>

            {/* Gelişmiş Filtreler */}
            {advancedFiltersVisible && (
              <Card style={{ marginBottom: 16 }}>
                <Collapse defaultActiveKey={['1']}>
                  <Panel header="Detaylı Filtreleme" key="1">
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12} md={8}>
                        <Text strong>Yetkinlik</Text>
                        <Input
                          placeholder="Yetkinlik ara (örn: .NET Core, React)"
                          value={filters.skill || ''}
                          onChange={(e) => handleFilterChange('skill', e.target.value || undefined)}
                          allowClear
                        />
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Text strong>Onaylanmış Yetkinlik</Text>
                        <Input
                          placeholder="Onaylanmış yetkinlik ara (örn: .NET Core)"
                          value={filters.approvedSkill || ''}
                          onChange={(e) => handleFilterChange('approvedSkill', e.target.value || undefined)}
                          allowClear
                        />
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Text strong>Proje</Text>
                        <Input
                          placeholder="Proje adı ara"
                          value={filters.project || ''}
                          onChange={(e) => handleFilterChange('project', e.target.value || undefined)}
                          allowClear
                        />
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Text strong>Bootcamp Konusu</Text>
                        <Input
                          placeholder="Bootcamp konusu ara"
                          value={filters.bootcampTopic || ''}
                          onChange={(e) => handleFilterChange('bootcampTopic', e.target.value || undefined)}
                          allowClear
                        />
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Text strong>Mentör</Text>
                        <Select
                          placeholder="Mentör seçin"
                          value={filters.mentorId}
                          onChange={(value) => handleFilterChange('mentorId', value)}
                          style={{ width: '100%' }}
                          allowClear
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {mentors.map(mentor => (
                            <Option key={mentor.id} value={mentor.id}>
                              {mentor.firstName} {mentor.lastName}
                            </Option>
                          ))}
                        </Select>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Text strong>Min Mentor Saati</Text>
                        <InputNumber
                          placeholder="Min saat"
                          value={filters.minMentorHours}
                          onChange={(value) => handleFilterChange('minMentorHours', value || undefined)}
                          style={{ width: '100%' }}
                          min={0}
                        />
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Text strong>Max Mentor Saati</Text>
                        <InputNumber
                          placeholder="Max saat"
                          value={filters.maxMentorHours}
                          onChange={(value) => handleFilterChange('maxMentorHours', value || undefined)}
                          style={{ width: '100%' }}
                          min={0}
                        />
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Text strong>Min Deneyim (Yıl)</Text>
                        <InputNumber
                          placeholder="Min yıl"
                          value={filters.minExperience}
                          onChange={(value) => handleFilterChange('minExperience', value || undefined)}
                          style={{ width: '100%' }}
                          min={0}
                        />
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Text strong>Max Deneyim (Yıl)</Text>
                        <InputNumber
                          placeholder="Max yıl"
                          value={filters.maxExperience}
                          onChange={(value) => handleFilterChange('maxExperience', value || undefined)}
                          style={{ width: '100%' }}
                          min={0}
                        />
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Text strong>Lokasyon</Text>
                        <Input
                          placeholder="Lokasyon ara (örn: İstanbul, Ankara)"
                          value={filters.location || ''}
                          onChange={(e) => handleFilterChange('location', e.target.value || undefined)}
                          allowClear
                        />
                      </Col>
                      <Col xs={24}>
                        <Space>
                          <Button type="primary" onClick={() => refetchApplications()}>
                            Filtrele
                          </Button>
                          <Button onClick={handleClearFilters}>
                            Filtreleri Temizle
                          </Button>
                        </Space>
                      </Col>
                    </Row>
                  </Panel>
                </Collapse>
              </Card>
            )}

            <Table
              scroll={{ x: 'max-content' }}
              columns={applicationColumns}
              dataSource={applications}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} / ${total} başvuru`,
              }}
            />
          </TabPane>

          <TabPane tab="Mülakatlar" key="interviews">
            <Table
              scroll={{ x: 'max-content' }}
              columns={interviewColumns}
              dataSource={interviews}
              rowKey="id"
              loading={interviewsLoading}
              pagination={{
                current: interviewPage,
                pageSize: interviewPageSize,
                total: interviewTotal,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} / ${total} mülakat`,
                onChange: (page, pageSize) => {
                  setInterviewPage(page);
                },
                onShowSizeChange: (current, size) => {
                  setInterviewPage(1);
                },
              }}
            />
          </TabPane>
        </Tabs>
      </Card>
      </TabListLoading>

      {/* Başvuru Detay Modal */}
      <Modal
        title="Başvuru Detayları"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Kapat
          </Button>
        ]}
        width={800}
      >
        {selectedApplication && (
          <div>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Aday">
                <Text strong>{selectedApplication.applicantName || 'Bilinmiyor'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedApplication.applicantEmail}
              </Descriptions.Item>
              <Descriptions.Item label="Tip">
                {selectedApplication.applicantType}
              </Descriptions.Item>
              <Descriptions.Item label="İş İlanı">
                {selectedApplication.jobTitle}
              </Descriptions.Item>
              <Descriptions.Item label="Durum">
                <Tag color={getStatusColor(selectedApplication.status)}>
                  {getStatusText(selectedApplication.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Başvuru Tarihi">
                {dayjs(selectedApplication.appliedAt).format('DD.MM.YYYY HH:mm')}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <div>
              <Title level={5}>Ön Yazı</Title>
              <div style={{ 
                background: 'var(--bg-tertiary)', 
                padding: 16, 
                borderRadius: 6,
                whiteSpace: 'pre-wrap'
              }}>
                {selectedApplication.coverLetter}
              </div>
            </div>

            {selectedApplication.resumeFileName && (
              <>
                <Divider />
                <div>
                  <Title level={5}>CV</Title>
                  <Space>
                    <FileTextOutlined />
                    <Text>{selectedApplication.resumeFileName}</Text>
                  </Space>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Mülakat Modal */}
      <Modal
        title={editingInterview ? 'Mülakat Düzenle' : 'Mülakat Planla'}
        open={interviewModalVisible}
        onCancel={() => setInterviewModalVisible(false)}
        onOk={() => interviewForm.submit()}
        width={600}
      >
        <Form
          form={interviewForm}
          layout="vertical"
          onFinish={handleInterviewSubmit}
        >
          <Form.Item
            name="interviewDate"
            label="Mülakat Tarihi"
            rules={[{ required: true, message: 'Mülakat tarihi gereklidir' }]}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="DD.MM.YYYY HH:mm"
            />
          </Form.Item>

          <Form.Item
            name="interviewType"
            label="Mülakat Türü"
            rules={[{ required: true, message: 'Mülakat türü gereklidir' }]}
          >
            <Select>
              <Option value="Phone">Telefon</Option>
              <Option value="Video">Video</Option>
              <Option value="In-Person">Yüz Yüze</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="Durum"
            rules={[{ required: true, message: 'Durum gereklidir' }]}
          >
            <Select>
              <Option value="Scheduled">Planlandı</Option>
              <Option value="Completed">Tamamlandı</Option>
              <Option value="Cancelled">İptal Edildi</Option>
              <Option value="Rescheduled">Yeniden Planlandı</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="interviewerName"
            label="Mülakatçı Adı"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="location"
            label="Konum / Meeting Link"
          >
            <Input placeholder="Fiziksel adres veya video meeting linki" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notlar"
          >
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="interviewFeedback"
            label="Mülakat Değerlendirmesi"
          >
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="rating"
            label="Puan (1-5)"
          >
            <InputNumber min={1} max={5} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Candidate Detail Modal */}
      <Modal
        title={`${selectedCandidate?.applicantName || 'Aday'} - Detayları`}
        open={candidateDetailModalVisible}
        onCancel={() => {
          setCandidateDetailModalVisible(false);
          setSelectedCandidate(null);
        }}
        width={1000}
        footer={[
          <Button key="close" onClick={() => {
            setCandidateDetailModalVisible(false);
            setSelectedCandidate(null);
          }}>
            Kapat
          </Button>
        ]}
      >
        {candidateDetailLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : selectedCandidate?.detail ? (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card title="Kişisel Bilgiler" size="small">
                  <Row gutter={[16, 8]}>
                    <Col span={12}>
                      <Text strong>Ad Soyad:</Text> {selectedCandidate.detail.firstName} {selectedCandidate.detail.lastName}
                    </Col>
                    <Col span={12}>
                      <Text strong>E-posta:</Text> {selectedCandidate.detail.email}
                    </Col>
                    <Col span={12}>
                      <Text strong>Telefon:</Text> {selectedCandidate.detail.phoneNumber || 'Belirtilmemiş'}
                    </Col>
                    <Col span={12}>
                      <Text strong>Şehir:</Text> {selectedCandidate.detail.city || 'Belirtilmemiş'}
                    </Col>
                    {selectedCandidate.applicantType === 'Mentor' && selectedCandidate.detail.title && (
                      <Col span={12}>
                        <Text strong>Ünvan:</Text> {selectedCandidate.detail.title}
                      </Col>
                    )}
                    {selectedCandidate.applicantType === 'Mentor' && selectedCandidate.detail.hourlyRate && (
                      <Col span={12}>
                        <Text strong>Saatlik Ücret:</Text> ₺{selectedCandidate.detail.hourlyRate}
                      </Col>
                    )}
                    <Col span={24}>
                      <Text strong>Bio:</Text> {selectedCandidate.detail.bio || 'Belirtilmemiş'}
                    </Col>
                  </Row>
                </Card>
              </Col>
              
              {(selectedCandidate.detail.websiteUrl || selectedCandidate.detail.linkedInProfile || selectedCandidate.detail.githubProfile) && (
                <Col span={24}>
                  <Card title="İletişim Bilgileri" size="small">
                    <Descriptions column={2} bordered>
                      {selectedCandidate.detail.websiteUrl && (
                        <Descriptions.Item label="Website" span={2}>
                          <a href={selectedCandidate.detail.websiteUrl} target="_blank" rel="noopener noreferrer">
                            {selectedCandidate.detail.websiteUrl}
                          </a>
                        </Descriptions.Item>
                      )}
                      {selectedCandidate.detail.linkedInProfile && (
                        <Descriptions.Item label="LinkedIn" span={2}>
                          <a href={selectedCandidate.detail.linkedInProfile} target="_blank" rel="noopener noreferrer">
                            {selectedCandidate.detail.linkedInProfile}
                          </a>
                        </Descriptions.Item>
                      )}
                      {selectedCandidate.detail.githubProfile && (
                        <Descriptions.Item label="GitHub" span={2}>
                          <a href={selectedCandidate.detail.githubProfile} target="_blank" rel="noopener noreferrer">
                            {selectedCandidate.detail.githubProfile}
                          </a>
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Card>
                </Col>
              )}

              {selectedCandidate.detail.skills && selectedCandidate.detail.skills.length > 0 && (
                <Col span={24}>
                  <Card title="Yetenekler" size="small">
                    <Space wrap>
                      {selectedCandidate.detail.skills.map((skill: any, index: number) => (
                        <Tag key={index} color="blue">{skill.skillName}</Tag>
                      ))}
                    </Space>
                  </Card>
                </Col>
              )}

              {selectedCandidate.detail.workExperiences && selectedCandidate.detail.workExperiences.length > 0 && (
                <Col span={24}>
                  <Card title="İş Deneyimi" size="small">
                    {selectedCandidate.detail.workExperiences.map((work: any, index: number) => (
                      <div key={index} style={{ marginBottom: '12px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <Text strong>{work.companyName}</Text> - {work.position}
                        <br />
                        <Text type="secondary">{dayjs(work.startDate).format('MMM YYYY')} - {(work.isCurrentJob || !work.endDate) ? 'Günümüz' : dayjs(work.endDate).format('MMM YYYY')}</Text>
                        {work.description && <div style={{ marginTop: '4px' }}>{work.description}</div>}
                      </div>
                    ))}
                  </Card>
                </Col>
              )}

              {selectedCandidate.detail.educations && selectedCandidate.detail.educations.length > 0 && (
                <Col span={24}>
                  <Card title="Eğitim Bilgileri" size="small">
                    {selectedCandidate.detail.educations.map((edu: any, index: number) => (
                      <div key={index} style={{ marginBottom: '12px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <Text strong>{edu.schoolName}</Text> - {edu.degree || edu.department}
                        <br />
                        <Text type="secondary">{(edu.department || edu.fieldOfStudy || '')} • {dayjs(edu.startDate).format('YYYY')} - {edu.endDate ? dayjs(edu.endDate).format('YYYY') : (edu.isGraduated ? '-' : 'Günümüz')}</Text>
                        {edu.description && <div style={{ marginTop: '4px' }}>{edu.description}</div>}
                      </div>
                    ))}
                  </Card>
                </Col>
              )}

              {selectedCandidate.detail.projects && selectedCandidate.detail.projects.length > 0 && (
                <Col span={24}>
                  <Card title="Projeler" size="small">
                    {selectedCandidate.detail.projects.map((project: any, index: number) => (
                      <div key={index} style={{ marginBottom: '12px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <Text strong>{project.projectName}</Text>
                        <br />
                        <Text type="secondary">{dayjs(project.startDate).format('MMM YYYY')} - {project.endDate ? dayjs(project.endDate).format('MMM YYYY') : 'Devam ediyor'}</Text>
                        {project.projectDescription && <div style={{ marginTop: '4px' }}>{project.projectDescription}</div>}
                        {project.technologies && (
                          <div style={{ marginTop: '4px' }}>
                            <Space wrap>
                              {(Array.isArray(project.technologies) ? project.technologies : project.technologies.split(',')).map((tech: string, techIndex: number) => (
                                <Tag key={techIndex} color="green">{tech.trim()}</Tag>
                              ))}
                            </Space>
                          </div>
                        )}
                      </div>
                    ))}
                  </Card>
                </Col>
              )}

              {selectedCandidate.detail.certificates && selectedCandidate.detail.certificates.length > 0 && (
                <Col span={24}>
                  <Card title="Sertifikalar" size="small">
                    {selectedCandidate.detail.certificates.map((cert: any, index: number) => (
                      <div key={index} style={{ marginBottom: '12px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <Text strong>{cert.certificateName}</Text>
                        <br />
                        <Text type="secondary">{cert.issuingOrganization} • {dayjs(cert.issueDate).format('MMM YYYY')}</Text>
                        {cert.description && <div style={{ marginTop: '4px' }}>{cert.description}</div>}
                      </div>
                    ))}
                  </Card>
                </Col>
              )}

              {selectedCandidate.applicantType === 'Mentor' && selectedCandidate.detail.favouriteMentees && selectedCandidate.detail.favouriteMentees.length > 0 && (
                <Col span={24}>
                  <Card title="Referans verdiği menteeler" size="small">
                    <Space wrap>
                      {selectedCandidate.detail.favouriteMentees.map((m: { id: number; firstName: string; lastName: string }) => (
                        <Button
                          key={m.id}
                          type="link"
                          size="small"
                          onClick={() => window.open(`/mentee/${m.id}`, '_blank')}
                        >
                          {m.firstName} {m.lastName}
                        </Button>
                      ))}
                    </Space>
                  </Card>
                </Col>
              )}
              {selectedCandidate.applicantType === 'Mentee' && selectedCandidate.detail.referencedByMentors && selectedCandidate.detail.referencedByMentors.length > 0 && (
                <Col span={24}>
                  <Card title="Referansları" size="small">
                    <Space wrap>
                      {selectedCandidate.detail.referencedByMentors.map((m: { id: number; firstName: string; lastName: string }) => (
                        <Button
                          key={m.id}
                          type="link"
                          size="small"
                          onClick={() => window.open(`/mentor/${m.id}`, '_blank')}
                        >
                          {m.firstName} {m.lastName}
                        </Button>
                      ))}
                    </Space>
                  </Card>
                </Col>
              )}
            </Row>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Text>Aday detayları yüklenemedi</Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ApplicationsTab;